const AGGREGATION_LABEL_PREFIX = {
    avg: 'Avg',
    countDistinct: 'Distinct',
    max: 'Max',
    min: 'Min',
    sum: 'Total',
};
const AGGREGATION_NAME_PREFIX = {
    avg: 'avg',
    countDistinct: 'distinct',
    max: 'max',
    min: 'min',
    sum: 'sum',
};
/**
 * Generates measure metadata from dimensions that have an `aggregations` array.
 *
 * For each dimension with `aggregations`, one measure entry is created per
 * aggregation type using a consistent naming convention:
 *
 * - `avg`            → `{cube}.avg_{dim}`           (label: "Avg {dim label}")
 * - `sum`            → `{cube}.sum_{dim}`            (label: "Total {dim label}")
 * - `min`            → `{cube}.min_{dim}`            (label: "Min {dim label}")
 * - `max`            → `{cube}.max_{dim}`            (label: "Max {dim label}")
 * - `countDistinct`  → `{cube}.distinct_{dim}`       (label: "Distinct {dim label}")
 *
 * The corresponding Cube.js measure must use the same naming convention and
 * can reference the dimension SQL via `${dimension_name}`.
 *
 * @example
 * ```ts
 * const dims = {
 *   'meetings.duration_minutes': {
 *     label: 'Duration (Minutes)',
 *     type: 'number' as const,
 *     aggregations: ['avg', 'max'] as const,
 *   },
 * };
 * // Produces:
 * // { 'meetings.avg_duration_minutes': { label: 'Avg Duration (Minutes)', type: 'number' },
 * //   'meetings.max_duration_minutes': { label: 'Max Duration (Minutes)', type: 'number' } }
 * ```
 */
export const generateMeasuresFromDimensions = (dimensions) => {
    const measures = {};
    for (const [key, meta] of Object.entries(dimensions)) {
        if (!meta.aggregations?.length)
            continue;
        const dotIndex = key.indexOf('.');
        const cubeName = key.slice(0, dotIndex);
        const dimName = key.slice(dotIndex + 1);
        for (const agg of meta.aggregations) {
            const prefix = AGGREGATION_NAME_PREFIX[agg];
            const labelPrefix = AGGREGATION_LABEL_PREFIX[agg];
            measures[`${cubeName}.${prefix}_${dimName}`] = {
                label: `${labelPrefix} ${meta.label}`,
                type: 'number',
            };
        }
    }
    return measures;
};
